import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { apiClient, apiErrorMessage } from '@/lib/api'
import { Link } from 'react-router-dom'
import { useAuth } from '@/stores/authStore'

type Member = {
  id: string
  name: string
  email: string
  role: string
  disabled: boolean
  createdAt: string
  skillCount: number
}

type Invite = {
  id: string
  email: string
  token: string
  expiresAt: string
  acceptedAt: string | null
  revokedAt: string | null
  emailSentAt: string | null
  createdAt: string
  inviter: { id: string; name: string; email: string }
}

export default function WorkspacePage() {
  const { workspace } = useAuth()
  const qc = useQueryClient()
  const [inviteEmail, setInviteEmail] = useState('')

  const members = useQuery({
    queryKey: ['workspace', 'members'],
    queryFn: async () =>
      (await apiClient.get<{ data: Member[] }>('/api/v1/workspace/members')).data,
  })
  const invites = useQuery({
    queryKey: ['workspace', 'invites'],
    queryFn: async () =>
      (await apiClient.get<{ data: Invite[] }>('/api/v1/workspace/invites')).data,
  })

  const inviteMut = useMutation({
    mutationFn: async (email: string) => {
      await apiClient.post('/api/v1/workspace/invite', { email })
    },
    onSuccess: () => {
      setInviteEmail('')
      qc.invalidateQueries({ queryKey: ['workspace', 'invites'] })
    },
  })

  const revokeMut = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/workspace/invites/${id}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace', 'invites'] }),
  })

  const roleMut = useMutation({
    mutationFn: async (vars: { id: string; role: string }) => {
      await apiClient.patch(`/api/v1/workspace/members/${vars.id}`, { role: vars.role })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace', 'members'] }),
  })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Workspace: {workspace?.name}</h1>
        <p className="text-sm text-neutral-500">
          <Link to="/settings/privacy" className="underline">
            Privacy & data
          </Link>
        </p>
      </div>

      <section className="rounded border bg-white p-4">
        <h2 className="font-semibold mb-3">Invite member</h2>
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (inviteEmail.trim()) inviteMut.mutate(inviteEmail.trim())
          }}
        >
          <input
            type="email"
            required
            placeholder="email@example.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            className="flex-1 rounded border px-2 py-1.5 text-sm"
          />
          <button
            type="submit"
            disabled={inviteMut.isPending}
            className="rounded bg-neutral-900 text-white text-sm px-3 py-1.5 disabled:opacity-50"
          >
            {inviteMut.isPending ? 'Inviting…' : 'Invite'}
          </button>
        </form>
        {inviteMut.error && (
          <p className="mt-2 text-xs text-red-600">{apiErrorMessage(inviteMut.error)}</p>
        )}
      </section>

      <section className="rounded border bg-white p-4">
        <h2 className="font-semibold mb-3">Pending invites</h2>
        {(invites.data?.data ?? []).length === 0 ? (
          <p className="text-sm text-neutral-500">No invites.</p>
        ) : (
          <ul className="divide-y text-sm">
            {(invites.data?.data ?? []).map((i) => {
              const state = i.acceptedAt
                ? 'accepted'
                : i.revokedAt
                ? 'revoked'
                : new Date(i.expiresAt) < new Date()
                ? 'expired'
                : 'pending'
              return (
                <li key={i.id} className="py-2 flex items-center justify-between">
                  <div>
                    <span className="font-medium">{i.email}</span>
                    <span className="text-xs text-neutral-500 ml-2">{state}</span>
                  </div>
                  {state === 'pending' && (
                    <button
                      onClick={() => revokeMut.mutate(i.id)}
                      disabled={revokeMut.isPending}
                      className="text-xs text-red-700 hover:underline"
                    >
                      Revoke
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <section className="rounded border bg-white p-4">
        <h2 className="font-semibold mb-3">Members</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-neutral-500 border-b">
              <tr>
                <th className="px-2 py-2">Name</th>
                <th className="px-2 py-2">Email</th>
                <th className="px-2 py-2">Role</th>
                <th className="px-2 py-2">Skills</th>
              </tr>
            </thead>
            <tbody>
              {(members.data?.data ?? []).map((m) => (
                <tr key={m.id} className="border-b">
                  <td className="px-2 py-2">{m.name}</td>
                  <td className="px-2 py-2 text-neutral-600">{m.email}</td>
                  <td className="px-2 py-2">
                    {['OWNER', 'SUPERADMIN'].includes(m.role) ? (
                      <span>{m.role}</span>
                    ) : (
                      <select
                        value={m.role}
                        onChange={(e) =>
                          roleMut.mutate({ id: m.id, role: e.target.value })
                        }
                        className="rounded border px-1 py-0.5 text-xs"
                      >
                        {['MEMBER', 'EDITOR', 'REVIEWER', 'COMPLIANCE'].map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="px-2 py-2">{m.skillCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}
